import type { Participant, ParticipantRole } from "@/services/project-service"

import { api } from "@/lib/api-client"

export type DataConnectionDriverType = "pymysql"

export type DataConnectionCredentialRef = {
  secret_name: string
  vault_name?: string
  version?: string | null
}

export type DataConnection = {
  id: string
  name: string
  driver_type: DataConnectionDriverType
  host: string
  port: number
  database: string
  username: string
  enabled: boolean
  credential_ref?: DataConnectionCredentialRef
  created_at?: string
  updated_at?: string
  /**
   * Users/groups granted access to this connection. Present on the list
   * response (the connection record stores it); used to show a
   * "shared with N" indicator without opening the share dialog.
   */
  participants?: Participant[]
}

export type CreateDataConnectionInput = {
  id: string
  name: string
  driver_type: DataConnectionDriverType
  host: string
  port: number
  database?: string
  username?: string
  password?: string
  enabled?: boolean
}

export type UpdateDataConnectionInput = {
  name?: string
  host?: string
  port?: number
  database?: string
  username?: string
  password?: string
  enabled?: boolean
}

export type TestDataConnectionResult = {
  success: boolean
  latency_ms?: number
  error?: string
}

type DataConnectionsListResponse = {
  connections: DataConnection[]
}

type DataConnectionMutationResponse = {
  success: boolean
  connection: DataConnection
}

export async function listDataConnections(): Promise<DataConnection[]> {
  const response = await api.get("/admin/data-connections")
  const data = (response.data || response) as DataConnectionsListResponse
  return Array.isArray(data.connections) ? data.connections : []
}

export async function createDataConnection(
  input: CreateDataConnectionInput
): Promise<DataConnection> {
  const response = await api.post("/admin/data-connections", input)
  const data = (response.data || response) as DataConnectionMutationResponse
  return data.connection
}

export async function updateDataConnection(
  connectionId: string,
  input: UpdateDataConnectionInput
): Promise<DataConnection> {
  const response = await api.put(
    `/admin/data-connections/${encodeURIComponent(connectionId)}`,
    input
  )
  const data = (response.data || response) as DataConnectionMutationResponse
  return data.connection
}

export async function deleteDataConnection(connectionId: string): Promise<void> {
  await api.delete(`/admin/data-connections/${encodeURIComponent(connectionId)}`)
}

export async function testDataConnection(connectionId: string): Promise<TestDataConnectionResult> {
  const response = await api.post(
    `/admin/data-connections/${encodeURIComponent(connectionId)}/test`,
    {}
  )
  const data = (response.data || response) as TestDataConnectionResult
  return data
}

export type IntrospectDataConnectionResult = {
  scheduled: boolean
  connection_id: string
}

export type DataConnectionColumn = {
  name: string
  type: string
  nullable?: boolean
  example_values?: string[]
  distinct_count_estimate?: number | null
  description?: string | null
}

export type DataConnectionTable = {
  row_count_estimate?: number | null
  description?: string | null
  columns?: DataConnectionColumn[]
}

export type DataConnectionSchemaEntry = {
  description?: string | null
  tables?: Record<string, DataConnectionTable>
}

/** Sparse delta for PATCH /schema/annotations. Missing keys are left as-is;
 *  null clears an existing description. */
export type SchemaAnnotationsDelta = {
  summary?: string | null
  schemas?: Record<
    string,
    {
      description?: string | null
      tables?: Record<
        string,
        {
          description?: string | null
          columns?: Record<string, { description?: string | null }>
        }
      >
    }
  >
}

export type DataConnectionSchemaStatus = {
  connection_id: string
  introspected_at?: string
  status?: "success" | "partial" | "failed"
  error?: string | null
  summary?: string | null
  schema_count?: number
  table_count?: number
  schemas?: Record<string, DataConnectionSchemaEntry>
}

export async function introspectDataConnection(
  connectionId: string
): Promise<IntrospectDataConnectionResult> {
  const response = await api.post(
    `/admin/data-connections/${encodeURIComponent(connectionId)}/introspect`,
    {}
  )
  return (response.data || response) as IntrospectDataConnectionResult
}

export async function getDataConnectionSchema(
  connectionId: string
): Promise<DataConnectionSchemaStatus | null> {
  try {
    const response = await api.get(
      `/admin/data-connections/${encodeURIComponent(connectionId)}/schema`
    )
    return (response.data || response) as DataConnectionSchemaStatus
  } catch (err: unknown) {
    // 404 just means no catalog yet — treat as null so the UI can render
    // an "unknown / never crawled" state instead of an error.
    if (
      typeof err === "object" &&
      err &&
      "status" in err &&
      (err as { status?: number }).status === 404
    ) {
      return null
    }
    throw err
  }
}

export async function updateDataConnectionAnnotations(
  connectionId: string,
  delta: SchemaAnnotationsDelta
): Promise<DataConnectionSchemaStatus> {
  const response = await api.patch(
    `/admin/data-connections/${encodeURIComponent(connectionId)}/schema/annotations`,
    delta
  )
  return (response.data || response) as DataConnectionSchemaStatus
}

// ---------- Sharing — assign user/group access to a data connection ----------

export type DataConnectionParticipantsResponse = {
  participants: Participant[]
}

export async function listDataConnectionParticipants(connectionId: string): Promise<Participant[]> {
  const response = await api.get(
    `/admin/data-connections/${encodeURIComponent(connectionId)}/participants`
  )
  const data = (response.data || response) as DataConnectionParticipantsResponse
  return Array.isArray(data.participants) ? data.participants : []
}

export async function addDataConnectionParticipant(
  connectionId: string,
  participant: {
    oid: string
    displayName: string
    type: "user" | "group"
    role: ParticipantRole
  }
): Promise<Participant> {
  const response = await api.post(
    `/admin/data-connections/${encodeURIComponent(connectionId)}/participants`,
    participant
  )
  const data = (response.data || response) as { participant: Participant }
  return data.participant
}

export async function updateDataConnectionParticipantRole(
  connectionId: string,
  participantOid: string,
  role: ParticipantRole
): Promise<void> {
  await api.put(
    `/admin/data-connections/${encodeURIComponent(connectionId)}/participants/${encodeURIComponent(
      participantOid
    )}`,
    { role }
  )
}

export async function removeDataConnectionParticipant(
  connectionId: string,
  participantOid: string
): Promise<void> {
  await api.delete(
    `/admin/data-connections/${encodeURIComponent(connectionId)}/participants/${encodeURIComponent(
      participantOid
    )}`
  )
}

// ---------- Non-admin: list connections the caller is allowed to use --------

export async function listAvailableDataConnections(): Promise<DataConnection[]> {
  const response = await api.get("/data-connections/available")
  const data = (response.data || response) as DataConnectionsListResponse
  return Array.isArray(data.connections) ? data.connections : []
}
